import { Table, type TableProps } from 'antd';
import { pick } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type VariantConfiguratorListItemFragment } from '../../../../api/fragments';
import { useListVariantConfiguratorsQuery } from '../../../../api/queries/listVariantConfigurators';
import {
    VariantConfiguratorSortingField,
    SortingOrder,
    type VariantConfiguratorFilteringRule,
} from '../../../../api/types';
import PaginatedTable from '../../../../components/PaginatedTable';
import InternalErrorResult from '../../../../components/results/InternalErrorResult';
import { useListReducer } from '../../../../components/useListReducer';
import makeGetSortingRule from '../../../../utilities/makeGetSortingRule';
import { TableSortingOrder } from '../../../../utilities/useSortAndFilterCache';
import { type HeaderTabs } from '../ModelConfiguratorDetailsInnerPage';

export const getSortField = makeGetSortingRule((field): VariantConfiguratorSortingField => {
    switch (field) {
        case 'displayName':
            return VariantConfiguratorSortingField.DisplayName;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});

export type VariantConfiguratorListProps = {
    modelConfiguratorId: string;
    activeTab: HeaderTabs;
};

const VariantConfiguratorList = ({ modelConfiguratorId, activeTab }: VariantConfiguratorListProps) => {
    const { t } = useTranslation(['variantConfiguratorList']);
    const [state, dispatch] = useListReducer<VariantConfiguratorSortingField, VariantConfiguratorFilteringRule>({
        sort: {
            field: VariantConfiguratorSortingField.DisplayName,
            order: SortingOrder.Asc,
            orderValue: TableSortingOrder.Asc,
        },
        filter: {},
    });

    const navigate = useNavigate();

    const { data, loading, error } = useListVariantConfiguratorsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            modelConfiguratorId,
            pagination: { offset: (state.page - 1) * state.pageSize, limit: state.pageSize },
            sort: pick(['field', 'order'], state.sort),
            filter: state.filter,
        },
    });

    const onChange = useCallback<TableProps<VariantConfiguratorListItemFragment>['onChange']>(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort': {
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });
                }

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filteringRule: {
                            displayName: !filters.displayName ? null : (filters.displayName[0] as string),
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    if (!loading && error) {
        return <InternalErrorResult />;
    }

    return (
        <PaginatedTable<VariantConfiguratorListItemFragment>
            dataSource={data?.lists.items ?? []}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: VariantConfiguratorListItemFragment) => ({
                onClick: () => {
                    navigate(`/admin/configurators/${modelConfiguratorId}/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey={row => row.id}
            state={state}
            tableName={t('variantConfiguratorList:title')}
            total={data?.lists.count ?? 0}
        >
            <Table.Column<VariantConfiguratorListItemFragment>
                key="displayName"
                dataIndex="displayName"
                title={t('variantConfiguratorList:columns.displayName')}
            />
        </PaginatedTable>
    );
};

export default VariantConfiguratorList;
